/* eslint-disable jsx-a11y/anchor-is-valid */
import {Modal, OverlayTrigger, ToggleButton, ToggleButtonGroup, Tooltip} from 'react-bootstrap'
import {createPortal} from 'react-dom'
import {KTSVG} from '../../../../template/helpers'
import {DataCDR} from '../../core/_models'
import WaveSurfer from 'wavesurfer.js'
import {useEffect, useRef, useState} from 'react'
import clsx from 'clsx'
import {SttMessageModel, TalkInnerTab} from './TalkInnerTab'
import {formatDateByZone} from '../../../../template/helpers/FormatDateZoneHelpers'
import {usersAPI} from '../../../../services/UsersService'
import {useQueryCDREntitiesRequest} from '../../hooks/useQueryCDRUsersRequest'
import {ModalClient} from './ModalClient'
import {LlmInnerTab} from './LlmInnerTab'
import {cdrAPI} from '../../../../services/CDRService'

const modalsRoot = document.getElementById('root-modals') || document.body

function CDRModalAi({
  data,
  recordUrl,
  show,
  handleClose,
}: {
  data: DataCDR
  recordUrl?: string
  show: boolean
  handleClose: () => void
}) {
  const [speed, setSpeed] = useState(1)
  const [mute, setMute] = useState<boolean>(false)
  const {state} = useQueryCDREntitiesRequest()
  const {data: currentUser} = usersAPI.useFetchGetMeQuery()
  const [currentTime, setCurrentTime] = useState<number>(0)
  const [waveSurfer, setWaveSurfer] = useState<WaveSurfer>()
  const [isPlayPause, setIsPlayPause] = useState<boolean>(false)
  const [isAutoScrollEnabled, setIsAutoScrollEnabled] = useState<boolean>(true)
  const [toggleButtonValue] = useState<string>('talk')
  const [downloadRecordBlob, setDownloadRecordBlob] = useState<string | undefined>()
  const {data: blobRecord, isLoading} = cdrAPI.useGetRecordingBlobQuery(data.id, {skip: !show})
  const waveformRef = useRef<HTMLDivElement | null>(null)
  const isAutoScrollEnabledRef = useRef<boolean>(true)
  const userName = Object.fromEntries(state.users.map((u) => [u.number, u.name]))
  const from_ = userName[data.from_!] || data.from_

  useEffect(() => {
    isAutoScrollEnabledRef.current = isAutoScrollEnabled
  }, [isAutoScrollEnabled])

  useEffect(() => {
    if (show && blobRecord && waveformRef.current) {
      const downloadUrl = URL.createObjectURL(blobRecord)
      setDownloadRecordBlob(downloadUrl)

      const wavesurfer = WaveSurfer.create({
        container: waveformRef.current,
        waveColor: '#50b8f3',
        progressColor: '#B5B5C3',
        audioRate: speed,
        normalize: true,
        height: 80,
      })

      let lastSec = -1

      const onAudioProcess = (currentTime: number) => {
        const currentSec = Math.floor(currentTime)
        if (lastSec === currentSec) return
        lastSec = currentSec
        const activeElement = document.querySelector(`[data-sec="${currentSec}"]`) as HTMLElement | null
        if (!activeElement) return
        if (isAutoScrollEnabledRef.current) {
          scrollToTranscriptElement(activeElement)
        }
        document.querySelectorAll('[data-sec]').forEach((el) => el.classList.remove('active-transcript'))
        activeElement.classList.add('active-transcript')
      }

      wavesurfer.loadBlob(blobRecord).catch((e) => {
        wavesurfer.destroy()
      })
      wavesurfer.on('timeupdate', (currentTime) => {
        setCurrentTime(parseFloat(currentTime.toFixed(2)))
      })
      wavesurfer.on('audioprocess', onAudioProcess)
      wavesurfer.on('finish', function () {
        setIsPlayPause(false)
        setMute(false)
      })
      wavesurfer.on('play', function () {
        setIsPlayPause(true)
      })
      wavesurfer.on('pause', function () {
        setIsPlayPause(false)
      })

      setWaveSurfer(wavesurfer)

      return () => {
        wavesurfer.destroy()
        setCurrentTime(0)
        setIsPlayPause(false)
        setMute(false)
        setDownloadRecordBlob(undefined)
        URL.revokeObjectURL(downloadUrl)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blobRecord, show])

  const scrollToTranscriptElement = (target: HTMLElement) => {
    const container = document.getElementById('transcription-scroll') as HTMLElement | null

    if (!container) return

    const containerTop = container.getBoundingClientRect().top
    const targetTop = target.getBoundingClientRect().top

    container.scrollTo({
      top: container.scrollTop + targetTop - containerTop,
      behavior: 'smooth',
    })
  }

  function dateTime(date: string) {
    return formatDateByZone(date, currentUser?.language || 'en', currentUser?.time_zone || 'UTC')
  }

  function formatTime(seconds: number): string {
    if (seconds <= 0) return '00:00'

    const hrs = Math.floor(seconds / 3600)
    const mins = Math.floor((seconds % 3600) / 60)
    const secs = seconds % 60

    const hh = String(hrs).padStart(2, '0')
    const mm = String(mins).padStart(2, '0')
    const ss = String(secs).padStart(2, '0')

    return hrs > 0 ? `${hh}:${mm}:${ss}` : `${mm}:${ss}`
  }

  const PlayPause = () => {
    waveSurfer?.playPause()
  }

  const playSegment = (start?: number, end?: number) => {
    if (start === undefined || end === undefined) return

    waveSurfer?.play(start, end)
  }

  const stripHtml = (html?: string) => {
    const element = document.createElement('div')
    element.innerHTML = html || ''
    return element.textContent || element.innerText || ''
  }

  const getTalkText = () => {
    if (data.stt && data.stt.segments) {
      return data.stt.segments
        .map((message: SttMessageModel) => {
          const time = `${message.start} сек - ${message.end} сек`
          const channel = message.channel !== undefined ? `канал ${message.channel}` : 'канал'
          const segment = message.segment !== undefined ? `сегмент ${message.segment}` : 'сегмент'

          return `${segment}, ${channel}, ${time}\n${stripHtml(message.text)}`
        })
        .join('\n\n')
    }
  }

  const downloadTalkTxt = () => {
    const blob = new Blob([getTalkText()], {type: 'text/plain;charset=utf-8'})
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')

    link.href = url
    link.download = `conversation-${data.id}.txt`
    link.click()

    URL.revokeObjectURL(url)
  }

  const copyTalkToClipboard = async () => {
    const text = getTalkText()

    if (navigator.clipboard) {
      await navigator.clipboard.writeText(text)
      return
    }

    const textarea = document.createElement('textarea')
    textarea.value = text
    textarea.style.position = 'fixed'
    textarea.style.opacity = '0'
    document.body.appendChild(textarea)
    textarea.select()
    document.execCommand('copy')
    document.body.removeChild(textarea)
  }

  return createPortal(
    <Modal show={show} onHide={handleClose} centered size='xl'>
      <Modal.Header>
        <Modal.Title>
          <i className='bi bi-stars fs-3 me-1 text-info' />
          Анализ звонка
        </Modal.Title>
        <span className='cursor-pointer' onClick={handleClose}>
          <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-1' />
        </span>
      </Modal.Header>
      <Modal.Body className='d-flex flex-column' style={{height: 'calc(100vh - 160px)', overflow: 'hidden'}}>
        <div className='d-flex flex-column align-items-stretch flex-grow-1' style={{minHeight: 0}}>
          {isLoading && (
            <div className='d-flex justify-content-center align-items-center' style={{minHeight: '135.5px'}}>
              <span className='indicator-progress' style={{display: 'block'}}>
                <span className='spinner-border spinner-border-sm align-middle text-primary fs-2 h-20px w-20px ms-2' />
              </span>
            </div>
          )}
          {blobRecord && (
            <>
              <div ref={waveformRef} id={`waveform_${data.id}`} className='wavesurfer waveform'></div>
              <div className='d-flex flex-center mt-4'>
                <div className='d-flex align-items-center me-2'>
                  <input
                    type='text'
                    min={0}
                    step={0.1}
                    className='form-control form-control-sm w-80px border-0 bg-body'
                    value={`${currentTime} сек`}
                    disabled
                  />
                </div>
                <label>
                  <select
                    className='form-select form-select-sm form-select-solid bg-body border-0'
                    value={speed}
                    onChange={(e) => {
                      setSpeed(parseFloat(e.target.value))
                      waveSurfer?.setPlaybackRate(parseFloat(e.target.value), true)
                    }}
                  >
                    <option value='0.5'>0.50x</option>
                    <option value='0.75'>0.75x</option>
                    <option value='1'>1.0x</option>
                    <option value='1.25'>1.25x</option>
                    <option value='1.5'>1.50x</option>
                    <option value='1.75'>1.75x</option>
                    <option value='2'>2.0x</option>
                  </select>
                </label>

                <button className='btn btn-icon mx-1' onClick={() => waveSurfer?.skip(-15)}>
                  <OverlayTrigger
                    delay={{show: 50, hide: 50}}
                    overlay={
                      <Tooltip id='tooltip-title' style={{position: 'fixed'}}>
                        - 15 сек
                      </Tooltip>
                    }
                  >
                    <i className='bi bi-arrow-counterclockwise fs-2 text-hover-primary' />
                  </OverlayTrigger>
                </button>

                <button className='btn btn-icon mx-6 play-pause' onClick={PlayPause}>
                  <i
                    className={clsx('fs-4x bi', {
                      'bi-play-fill text-gray-700 text-hover-primary': !isPlayPause,
                      'bi-pause-fill text-primary': isPlayPause,
                    })}
                  />
                </button>

                <button className='btn btn-icon mx-1' onClick={() => waveSurfer?.skip(15)}>
                  <OverlayTrigger
                    delay={{show: 50, hide: 50}}
                    overlay={
                      <Tooltip id='tooltip-title' style={{position: 'fixed'}}>
                        + 15 сек
                      </Tooltip>
                    }
                  >
                    <i className='bi bi-arrow-clockwise fs-2 text-hover-primary' />
                  </OverlayTrigger>
                </button>

                <OverlayTrigger
                  delay={{show: 50, hide: 50}}
                  overlay={
                    <Tooltip id='tooltip-title' style={{position: 'fixed'}}>
                      скачать
                    </Tooltip>
                  }
                >
                  <a href={downloadRecordBlob} download={data.id + '.wav'} className='btn btn-icon mx-1'>
                    <i className='bi bi-cloud-download fs-2' />
                  </a>
                </OverlayTrigger>
                <button
                  className='btn btn-icon'
                  onClick={() => {
                    waveSurfer?.setMuted(!mute)
                    setMute(!mute)
                  }}
                >
                  <OverlayTrigger
                    delay={{show: 50, hide: 50}}
                    overlay={
                      <Tooltip id='tooltip-title' style={{position: 'fixed'}}>
                        {/* {intl.formatMessage({id: 'CONTROLS.MUTE'})}  */}
                        без звука
                      </Tooltip>
                    }
                  >
                    <i className={clsx('bi bi-volume-mute fs-2x', {'text-primary': mute})} />
                  </OverlayTrigger>
                </button>
              </div>
            </>
          )}
          <div className='d-flex flex-row pt-5 gap-2 w-100'>
            <div className='flex-fill p-3 border rounded h-100'>
              <div className='text-muted small mb-1'>Дата вызова</div>
              <div className='fw-semibold'>{dateTime(data.call_start)}</div>
            </div>
            <div className='flex-fill p-3 border rounded h-100'>
              <div className='text-muted small mb-1'>Длительность</div>
              <div className='fw-semibold'>{formatTime(data.dial_duration)}</div>
            </div>
            <div className='flex-fill p-3 border rounded h-100'>
              <div className='text-muted small mb-1'>Звонок от</div>
              <div className='fw-semibold'>{from_}</div>
            </div>
            <div className='flex-fill p-3 border rounded h-100'>
              <ModalClient data={data.to_} clientInfo={data.client_info} />
            </div>
          </div>

          <div className='separator my-4' />

          <div className='d-flex justify-content-center bg-light-primary rounded-1 mb-3'>
            <ul className='nav nav-tabs nav-line-tabs nav-stretch fs-6 border-0 my-1'>
              <li className='nav-item'>
                <a className='nav-link active' data-bs-toggle='tab' href='#tab_stt'>
                  <span className='small text-gray-900'>Транскрипция</span>
                </a>
              </li>
              <li className='nav-item'>
                <a className='nav-link' data-bs-toggle='tab' href='#tab_llm'>
                  <span className='small text-gray-900'>AI Аналитика</span>
                </a>
              </li>
            </ul>
          </div>
          <div
            className='tab-content flex-column-fluid overflow-hidden'
            id='cdr-modal-ai'
            style={{minHeight: 0, flex: '1 1 auto'}}
          >
            <div className='tab-pane fade show active h-100 overflow-hidden' id='tab_stt' role='tabpanel'>
              <div className='h-100 d-flex flex-column overflow-hidden'>
                <div className='bg-gray-300 p-2 d-flex align-items-center justify-content-between rounded-2'>
                  <ToggleButtonGroup type='radio' name='options' defaultValue={'talk'} onChange={(val) => {}}>
                    <ToggleButton
                      id='tr-radio-1'
                      value={'talk'}
                      variant={toggleButtonValue === 'talk' ? 'primary' : 'outline-primary'}
                      size='sm'
                      className='d-flex align-items-center h-30px'
                      onClick={() => {}}
                      disabled
                    >
                      Разговор
                    </ToggleButton>
                  </ToggleButtonGroup>
                  <div className='d-flex align-items-center'>
                    <button
                      className={clsx('btn btn-sm h-30px me-1 d-flex align-items-center', {
                        'btn-outline btn-outline-dashed btn-outline-primary btn-active-light-primary':
                          isAutoScrollEnabled,
                        'btn-outline btn-outline-dashed btn-bg-light btn-color-muted': !isAutoScrollEnabled,
                      })}
                      title={isAutoScrollEnabled ? 'Отключить автоскролл' : 'Включить автоскролл'}
                      aria-pressed={isAutoScrollEnabled}
                      onClick={() => setIsAutoScrollEnabled((value) => !value)}
                    >
                      Авто прокрутка {isAutoScrollEnabled ? 'вкл.' : 'откл.'}
                    </button>
                    <button
                      className='btn btn-icon w-30px h-30px me-1 btn-outline btn-outline-dashed btn-outline-primary d-flex align-items-center justify-content-center'
                      title='Скачать разговор в TXT'
                      onClick={downloadTalkTxt}
                    >
                      <i className='bi bi-filetype-txt fs-2' />
                    </button>
                    <button
                      className='btn btn-icon w-30px h-30px me-1 btn-outline btn-outline-dashed btn-outline-primary d-flex align-items-center'
                      type='button'
                      title='Скопировать разговор'
                      onClick={copyTalkToClipboard}
                    >
                      <i className='bi bi-clipboard fs-2' />
                    </button>
                  </div>
                </div>
                <div className='d-flex gap-3 overflow-hidden pt-3 flex-grow-1' style={{minHeight: 0}}>
                  <div className='flex-row-fluid overflow-hidden' style={{minHeight: 0}}>
                    <div className='h-100'>
                      <TalkInnerTab stt={data.stt} playSegment={playSegment} />
                    </div>
                  </div>

                  {/* Правая колонка */}
                  <div className='flex-row-auto overflow-auto' style={{width: '20%', minHeight: 0}}>
                    {data.stt &&
                      data.stt.segments &&
                      data.stt.segments.map((message: SttMessageModel, i: number) => (
                        <div
                          key={message.segment ?? i}
                          className='d-flex align-items-center gap-1 mb-1 rounded px-2 py-1'
                          style={{backgroundColor: 'rgba(var(--kt-primary-rgb), 0.16)'}}
                        >
                          <button
                            className='btn btn-sm flex-grow-1 d-flex align-items-center text-start p-0'
                            style={{minWidth: 0}}
                            type='button'
                            onClick={() => {
                              const target = document.getElementById(`segment-${message.segment}`)
                              if (!target) return
                              scrollToTranscriptElement(target)
                            }}
                          >
                            <span className='d-block overflow-hidden' style={{minWidth: 0}}>
                              <span className='d-block text-truncate'>сегмент {message.segment}</span>
                              <span className='d-block text-muted text-truncate fs-8'>
                                {message.start} сек - {message.end} сек
                              </span>
                            </span>
                          </button>
                          {/* <button
                            className='btn btn-icon btn-sm btn-active-light-primary h-30px w-30px flex-shrink-0'
                            type='button'
                            title='Воспроизвести сегмент'
                            onClick={() => playSegment(message.start, message.end)}
                          >
                            <i className='bi bi-play-fill fs-2' />
                          </button> */}
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            </div>
            <div className='tab-pane fade h-100 overflow-hidden' id='tab_llm' role='tabpanel'>
              <LlmInnerTab data={data} />
            </div>
          </div>
        </div>
      </Modal.Body>
    </Modal>,
    modalsRoot
  )
}

export {CDRModalAi}
